import { handleActions, createAction } from "redux-actions";
import { axios } from "../cores/axiosWrapper";
import { getRoleActionsAPI } from "@modules/apis/rolesManagement_v2";
import { getUser } from "@modules/user-management-v2";
import { ASSET_SHARE_ACTIONS, CONSTANTS } from "@constants";
import i18n from "i18next";
import { capitalize } from "@utils/convert-string";
import extractApiError from "@utils/extract-api-error";

function getAuditsAPI(stageId, apiEndpoint, projectId, params) {
    const headers = {
        stageId,
    };

    if (projectId) {
        headers.projectId = projectId;
    }

    return axios.get(`${apiEndpoint}/v2/audit-logs`, {
        params,
        headers,
    });
}

export function getAuditAPI(stageId, apiEndpoint, id, projectId) {
    const headers = {
        stageId,
    };
    if (projectId) {
        headers.projectId = projectId;
    }
    return axios.get(`${apiEndpoint}/v2/audit-logs/${id}`, {
        headers,
    });
}

export function getAuditSearchKeysAPI(stageId, apiEndpoint, projectId) {
    const headers = {
        stageId,
    };

    if (projectId) {
        headers.projectId = projectId;
    }
    return axios.get(`${apiEndpoint}/v2/audit-logs/search-keys`, {
        headers,
    });
}

function getAssetAuditsAPI(stageId, apiEndpoint, projectId, resourceId, params) {
    const headers = {
        stageId,
    };

    if (projectId) {
        headers.projectId = projectId;
    }
    return axios.get(`${apiEndpoint}/assets/${resourceId}/histories`, {
        params,
        headers,
    });
}

function getAssetAuditAPI(stageId, apiEndpoint, projectId, resourceId, id) {
    const headers = {
        stageId,
    };
    if (projectId) {
        headers.projectId = projectId;
    }
    return axios.get(`${apiEndpoint}/assets/${resourceId}/histories/${id}`, {
        headers,
    });
}

const AUDITS_PENDING = "AUDITS_PENDING";
const AUDITS_FAILURE = "AUDITS_FAILURE";
const RESET_AUDITS_LOGS = "RESET_AUDITS_LOGS";
const GET_AUDITS_V2_FOR_LIST_SUCCESS = "GET_AUDITS_V2_FOR_LIST_SUCCESS";
const GET_AUDITS_V2_FOR_LIST_SUCCESS_TO_APPEND = "GET_AUDITS_V2_FOR_LIST_SUCCESS_TO_APPEND";
const GET_AUDITS_V2_FOR_DETAIL_SUCCESS = "GET_AUDITS_V2_FOR_DETAIL_SUCCESS";
const GET_AUDITS_V2_SEARCH_KEYS_SUCCESS = "GET_AUDITS_V2_SEARCH_KEYS_SUCCESS";
const GET_AUDITS_V2_ACTIONS_SUCCESS = "GET_AUDITS_V2_ACTIONS_SUCCESS";

const initialState = {
    pending: false,
    error: null,
    list: null,
    searchKeys: null,
    actions: null,
};

export const resetAuditLogs = createAction(RESET_AUDITS_LOGS);

export default handleActions(
    {
        [AUDITS_PENDING]: (state) => {
            return {
                ...state,
                pending: true,
                error: null,
            };
        },
        [AUDITS_FAILURE]: (state, action) => {
            return {
                ...state,
                pending: false,
                error: extractApiError(action.payload),
            };
        },
        [RESET_AUDITS_LOGS]: (state) => {
            return {
                ...state,
                pending: false,
                error: null,
                list: null,
            };
        },
        [GET_AUDITS_V2_FOR_LIST_SUCCESS]: (state, action) => {
            return {
                ...state,
                pending: false,
                error: null,
                list: action.payload,
            };
        },
        [GET_AUDITS_V2_FOR_LIST_SUCCESS_TO_APPEND]: (state, action) => {
            let data = action.payload;

            return {
                ...state,
                pending: false,
                error: null,
                list: { results: state.list.results.concat(data.results), nextToken: data.nextToken },
                exclusiveStartKey: data.exclusiveStartKey,
            };
        },
        [GET_AUDITS_V2_FOR_DETAIL_SUCCESS]: (state, action) => {
            let { data } = action.payload;

            return {
                ...state,
                pending: false,
                error: null,
                item: data,
            };
        },
        [GET_AUDITS_V2_SEARCH_KEYS_SUCCESS]: (state, action) => {
            const { data } = action.payload;
            return {
                ...state,
                searchKeys: data,
            };
        },
        [GET_AUDITS_V2_ACTIONS_SUCCESS]: (state, action) => {
            const { data } = action.payload;
            return {
                ...state,
                actions: data,
            };
        },
    },
    initialState,
);

const generateAuditLogSubject = (action) => {
    const { CREATE_DOWNLOAD_LINK, CREATE } = CONSTANTS("SHARE_AUDIT_ACTIONS");

    const parseAction = (action) => {
        switch (action) {
            case CREATE_DOWNLOAD_LINK:
                return "Shared externally";
            case CREATE:
                return "Shared internally";
            case "Download":
                return "Downloaded";
            default:
                return undefined;
        }
    };

    const parsedAction = parseAction(action);
    return parsedAction;
};
export const getAudits =
    ({ projectId, params }) =>
    (dispatch, getState) => {
        let { stage, auditsV2 } = getState();

        dispatch({ type: AUDITS_PENDING });
        return getAuditsAPI(stage.id, stage.endpoint, projectId, params)
            .then(async (response) => {
                const results = response.data?.results?.map((el) => ({
                    ...el,
                    subject: generateAuditLogSubject(el.action),
                }));
                if (!auditsV2.list) {
                    dispatch({
                        type: GET_AUDITS_V2_FOR_LIST_SUCCESS,
                        payload: { ...response.data, results },
                    });
                } else {
                    dispatch({
                        type: GET_AUDITS_V2_FOR_LIST_SUCCESS_TO_APPEND,
                        payload: { ...response.data, results },
                    });
                }
                return { ...response.data, results };
            })
            .catch((error) => {
                console.log(error);
                dispatch({
                    type: AUDITS_FAILURE,
                    payload: error,
                });
                return null;
            });
    };

const parseAssetsAuditData = async (action, data) => {
    const { payload } = data;
    const { UPDATE_OWNER, UPDATE_OWNER_IN_BULK } = CONSTANTS("ASSETS_AUDIT_ACTIONS");

    if (action === UPDATE_OWNER) {
        return i18n.t("common::msg::Update owner to {{name}}", { name: payload?.name });
    } else if (action === UPDATE_OWNER_IN_BULK) {
        return i18n.t("common::msg::Update owner to {{name}}", { name: payload?.owner?.name });
    }
};

const parseShareAuditData = async (action, data, dispatch, projectId) => {
    const { payload, pathParameters } = data;
    const {
        CREATE_DOWNLOAD_LINK,
        CREATE_SHARED_RESOURCE,
        CREATE_SHARED_RESOURCE_GRANTEE,
        CREATE,
        UPDATE_APPROVER_ACTION,
        UPDATE_SHARED_RESOURCE_GRANTEE,
        DELETE_SHARED_RESOURCE_GRANTEE,
    } = CONSTANTS("SHARE_AUDIT_ACTIONS");

    if (action === CREATE_DOWNLOAD_LINK) {
        const sharedToNames = payload.sharedTo?.map(({ data }) => data?.name ?? data?.email).join(", ");

        return i18n.t("common::msg::Shared link to {{names}}", { names: sharedToNames });
    } else if ([CREATE_SHARED_RESOURCE, CREATE_SHARED_RESOURCE_GRANTEE].includes(action)) {
        const limits = payload.limits;
        const grantees =
            action === CREATE_SHARED_RESOURCE
                ? payload.sharedResourceGrantees
                : [
                      {
                          ...payload.grantee,
                          action: payload.action,
                      },
                  ];

        const parsedGrantees = await Promise.all(
            grantees.map(async ({ grantee, actions }) => {
                const userHighestAction = actions[0];
                const userActionLabel = ASSET_SHARE_ACTIONS.find((action) => action.value === userHighestAction)?.label;

                const response = await dispatch(getUser(grantee.externalId, projectId));
                return {
                    ...grantee,
                    name: response.idp?.userInfo?.displayName,
                    action: i18n.t(`common::label::${userActionLabel}`),
                };
            }),
        );

        const granteesText = parsedGrantees.map((grantee) => `${grantee.name} - ${grantee.action}`).join(", ");

        let shareMessage = i18n.t("common::msg::Shared with {{grantees}} permission", { grantees: granteesText });

        if (limits.expiresAt) {
            shareMessage = i18n.t("common::msg::Shared with {{grantees}} permission until {{expireDate}}", {
                grantees: granteesText,
                expireDate: limits.expiresAt.split(" ")[0],
            });
        }

        if (limits.maxDownloads) {
            const baseKey = limits.expiresAt
                ? "common::msg::Shared with {{grantees}} permission until {{expireDate}} with max {{maxDownloads}} downloads"
                : "common::msg::Shared with {{grantees}} permission with max {{maxDownloads}} downloads";

            shareMessage = i18n.t(baseKey, {
                grantees: granteesText,
                expireDate: limits.expiresAt?.split(" ")[0],
                maxDownloads: limits.maxDownloads,
            });
        }

        return shareMessage;
    } else if (action === CREATE) {
        //Note: 내부공유 요청
        const approverUser = await dispatch(getUser(payload.approver.id, projectId));

        return i18n.t("common::msg::Share request to {{userName}}", {
            userName: approverUser.idp?.userInfo?.displayName,
        });
    } else if (action === UPDATE_APPROVER_ACTION) {
        return `${i18n.t(`common::label::${capitalize(pathParameters.status, true)}`)}`;
    } else if (action === UPDATE_SHARED_RESOURCE_GRANTEE) {
        //Note: 내부공유 사용자 권한 수정 (누구의 권한을 수정한 건지 알수가 없음)
        const { limits, actions } = payload;

        const userHighestAction = actions[0];
        const userActionLabel = ASSET_SHARE_ACTIONS.find((action) => action.value === userHighestAction)?.label;
        const permissionText = i18n.t(`common::label::${userActionLabel}`);

        let shareOptionsMessage = i18n.t("common::msg::Changed share options to {{permission}} permission", {
            permission: permissionText,
        });

        if (limits.expiresAt) {
            shareOptionsMessage = i18n.t(
                "common::msg::Changed share options to {{permission}} permission until {{expireDate}}",
                {
                    permission: permissionText,
                    expireDate: limits.expiresAt.split(" ")[0],
                },
            );
        }

        if (limits.maxDownloads) {
            const baseKey = limits.expiresAt
                ? "common::msg::Changed share options to {{permission}} permission until {{expireDate}} with max {{maxDownloads}} downloads"
                : "common::msg::Changed share options to {{permission}} permission with max {{maxDownloads}} downloads";

            shareOptionsMessage = i18n.t(baseKey, {
                permission: permissionText,
                expireDate: limits.expiresAt?.split(" ")[0],
                maxDownloads: limits.maxDownloads,
            });
        }

        return shareOptionsMessage;
    } else if (action === DELETE_SHARED_RESOURCE_GRANTEE) {
        //Note: 내부공유 사용자 권한 제거 (누구의 권한을 제거한 건지 알수가 없음)
    }
};

const parseAuditData = async (auditData, dispatch, projectId) => {
    const { domain: actionDomain, action, summary, requested } = auditData;

    if (!summary && !requested) {
        return "";
    }

    if (summary?.template && summary?.variables) {
        return i18n.t(`common::msg::${summary.template}`, summary.template, summary.variables);
    }

    const { SHARE, ASSETS } = CONSTANTS("AUDIT_DOMAINS");

    if (actionDomain === SHARE) {
        return await parseShareAuditData(action, requested, dispatch, projectId);
    } else if (actionDomain === ASSETS) {
        return await parseAssetsAuditData(action, requested, dispatch, projectId);
    }
    return "";
};

const getAuditAPIByDomain = (domain, type) => {
    const apiMap = {
        Assets: {
            list: getAssetAuditsAPI,
            detail: getAssetAuditAPI,
        },
        default: {
            list: getAuditsAPI,
            detail: getAuditAPI,
        },
    };

    const domainFunctions = apiMap[domain] || apiMap.default;
    return domainFunctions[type];
};

export const getAuditsWithDetail =
    ({ projectId, params, domain, resourceId, requiringDetailDomains = [] }) =>
    async (dispatch, getState) => {
        let { stage, auditsV2 } = getState();

        const getAuditsAPIFunction = getAuditAPIByDomain(domain, "list");
        const getAuditAPIFunction = getAuditAPIByDomain(domain, "detail");

        dispatch({ type: AUDITS_PENDING });
        try {
            const response = await getAuditsAPIFunction(stage.id, stage.endpoint, projectId, resourceId, params);

            const results = await Promise.all(
                response.data?.results?.map(async (el) => {
                    let auditItem = { ...el };

                    if (requiringDetailDomains.includes(el.domain)) {
                        try {
                            const { data } = await getAuditAPIFunction(
                                stage.id,
                                stage.endpoint,
                                projectId,
                                resourceId,
                                el.id,
                            );
                            auditItem = { ...auditItem, ...data };
                            auditItem.additionalDescription = await parseAuditData(auditItem, dispatch, projectId);
                        } catch (error) {
                            console.log(`상세 감사 로그 가져오기 실패: ${error}`);
                        }
                    }

                    return {
                        ...auditItem,
                        subject: generateAuditLogSubject(auditItem.action),
                    };
                }) || [],
            );

            const actionType = !auditsV2.list
                ? GET_AUDITS_V2_FOR_LIST_SUCCESS
                : GET_AUDITS_V2_FOR_LIST_SUCCESS_TO_APPEND;

            dispatch({
                type: actionType,
                payload: { ...response.data, results },
            });

            return { ...response.data, results };
        } catch (error) {
            console.log("감사 로그 가져오기 실패:", error);
            dispatch({
                type: AUDITS_FAILURE,
                payload: error,
            });
            return null;
        }
    };

export const getAudit =
    ({ projectId, id }) =>
    (dispatch, getState) => {
        let { stage } = getState();

        return getAuditAPI(stage.id, stage.endpoint, id, projectId)
            .then((response) => {
                dispatch({
                    type: GET_AUDITS_V2_FOR_DETAIL_SUCCESS,
                    payload: response,
                });
            })
            .catch((error) => {
                dispatch({
                    type: AUDITS_FAILURE,
                    payload: error,
                });
            });
    };

export const getAuditSearchKeys =
    ({ projectId }) =>
    (dispatch, getState) => {
        let { stage } = getState();

        return getAuditSearchKeysAPI(stage.id, stage.endpoint, projectId)
            .then((response) => {
                dispatch({
                    type: GET_AUDITS_V2_SEARCH_KEYS_SUCCESS,
                    payload: response,
                });
            })
            .catch((error) => {
                dispatch({
                    type: AUDITS_FAILURE,
                    payload: error,
                });
            });
    };

export const getRoleActions =
    ({ params }) =>
    (dispatch, getState) => {
        let { stage } = getState();

        return getRoleActionsAPI(stage.endpoint, params)
            .then((response) => {
                dispatch({
                    type: GET_AUDITS_V2_ACTIONS_SUCCESS,
                    payload: response,
                });
            })
            .catch((error) => {
                dispatch({
                    type: AUDITS_FAILURE,
                    payload: error,
                });
            });
    };
